import { useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Notice,
  formatRangeInZone,
  formatTimeInZone,
} from '@gatecheckpro/design-system';
import type { OrganizerEvent } from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useEventWorkspace } from './EventWorkspace.tsx';

/**
 * Sessions.
 *
 * The organizer enters local wall-clock times; the surface converts them to
 * instants using the *event's* timezone, not the browser's. Someone planning a
 * Lagos show from London must be able to type 19:00 and mean 19:00 in Lagos.
 */
function toInstant(localDate: string, localTime: string, timezone: string): string | null {
  if (localDate === '' || localTime === '') return null;
  // Find the UTC instant whose rendering in `timezone` is the wall clock typed.
  // Two passes converge because a zone offset changes by at most a couple of
  // hours, and the second pass lands on the right side of any DST boundary.
  const naive = new Date(`${localDate}T${localTime}:00Z`);
  let guess = naive;
  for (let pass = 0; pass < 2; pass += 1) {
    const rendered = new Date(
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
        .format(guess)
        .replace(/(\d+)\/(\d+)\/(\d+), (\d+):(\d+):(\d+)/, '$3-$1-$2T$4:$5:$6Z'),
    );
    guess = new Date(guess.getTime() + (naive.getTime() - rendered.getTime()));
  }
  return guess.toISOString();
}

export function EventSessionsPage() {
  const { event, organizationId, reload } = useEventWorkspace();
  const command = useCommand<OrganizerEvent>();
  const base = `/api/v1/organizer/organizations/${organizationId}/events/${event.eventId}`;

  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('19:00');
  const [endTime, setEndTime] = useState('23:00');
  const [doorsTime, setDoorsTime] = useState('18:00');

  const scheduled = event.sessions.filter((session) => session.status === 'SCHEDULED');

  async function addSession(formEvent: React.FormEvent): Promise<void> {
    formEvent.preventDefault();
    const startsAt = toInstant(date, startTime, event.timezone);
    const doorsOpenAt = doorsTime === '' ? null : toInstant(date, doorsTime, event.timezone);
    if (startsAt === null) return;

    // An end time earlier than the start means the session runs past midnight.
    const sameDayEnd = toInstant(date, endTime, event.timezone);
    const endsAt =
      sameDayEnd !== null && sameDayEnd > startsAt
        ? sameDayEnd
        : new Date(new Date(startsAt).getTime() + 4 * 3_600_000).toISOString();

    const result = await command.run(`${base}/sessions`, {
      method: 'POST',
      body: {
        name: name === '' ? null : name,
        startsAt,
        endsAt,
        doorsOpenAt,
      },
    });
    if (result !== null) {
      setName('');
      setDate('');
      reload();
    }
  }

  return (
    <div className="gc-stack gc-stack--loose">
      <Card title="Sessions" eyebrow="When this happens" flush>
        {scheduled.length === 0 ? (
          <div style={{ padding: '1.25rem' }}>
            <EmptyState title="No sessions yet">
              An event with no date cannot be attended, and cannot be published. Add the night — or
              the first of several.
            </EmptyState>
          </div>
        ) : (
          <div className="gc-table-scroll" style={{ border: 0, borderRadius: 0 }}>
            <table className="gc-table">
              <thead>
                <tr>
                  <th scope="col">Session</th>
                  <th scope="col">When</th>
                  <th scope="col">Doors</th>
                  <th scope="col">
                    <span className="gc-visually-hidden">Cancel</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {scheduled.map((session) => (
                  <tr key={session.eventSessionId}>
                    <td>
                      <Link to={`sessions/${session.eventSessionId}/seating`} relative="path">
                        {session.name ?? 'Unnamed'}
                      </Link>
                    </td>
                    <td>{formatRangeInZone(session.startsAt, session.endsAt, event.timezone)}</td>
                    <td>
                      {session.doorsOpenAt === null ? (
                        <span className="gc-muted">—</span>
                      ) : (
                        formatTimeInZone(session.doorsOpenAt, event.timezone)
                      )}
                    </td>
                    <td>
                      <Button
                        variant="destructive"
                        onClick={() =>
                          void command
                            .run(`${base}/sessions/${session.eventSessionId}`, {
                              method: 'DELETE',
                            })
                            .then((result) => {
                              if (result !== null) reload();
                            })
                        }
                      >
                        Cancel
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Add a session" eyebrow={`Times are in ${event.timezone}`}>
        <form className="gc-stack" onSubmit={(formEvent) => void addSession(formEvent)}>
          <FailureNotice error={command.error} />

          <Notice tone="info" title="Times belong to the venue">
            Whatever clock you are reading this on, these times mean {event.timezone}. That is what
            an attendee will see and what a gate will enforce.
          </Notice>

          <Field
            label="Name"
            hint="Optional. Useful for a festival — “Night 1”, “Matinee”."
            value={name}
            onChange={(input) => setName(input.target.value)}
          />
          <Field
            label="Date"
            type="date"
            required
            value={date}
            onChange={(input) => setDate(input.target.value)}
          />
          <div className="gc-grid">
            <Field
              label="Doors open"
              type="time"
              value={doorsTime}
              onChange={(input) => setDoorsTime(input.target.value)}
            />
            <Field
              label="Starts"
              type="time"
              required
              value={startTime}
              onChange={(input) => setStartTime(input.target.value)}
            />
            <Field
              label="Ends"
              type="time"
              required
              hint="Earlier than the start means it runs past midnight."
              value={endTime}
              onChange={(input) => setEndTime(input.target.value)}
            />
          </div>

          <Button type="submit" variant="primary" busy={command.busy} disabled={date === ''}>
            Add session
          </Button>
        </form>
      </Card>
    </div>
  );
}
